import classNames from 'classnames';
import { FC, useEffect, useRef } from 'react';
import { Bonus, makeFloat, useCurrency } from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { Info } from 'src/components/svg/General';
import { useModalContext } from 'src/hooks/useModalContext';

interface BonusCardProps {
  bonus: Bonus;
  handleSelectBonus: (bonus: Bonus | null) => void;
  selectedBonus: Bonus | null;
}

export const BonusCard: FC<BonusCardProps> = ({ bonus, handleSelectBonus, selectedBonus }) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { currencyIcon, currencyName } = useCurrency();
  const { closeModal } = useModalContext();

  const { id, percent, freeSpins, maxPayout, minDepositAmount, name } = bonus;

  const isBonusCardSelected = id === selectedBonus?.id;

  useEffect(() => {
    if (isBonusCardSelected) {
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [isBonusCardSelected]);

  return (
    <div
      ref={cardRef}
      key={bonus.id}
      onClick={() => {
        handleSelectBonus(selectedBonus?.id === id ? null : bonus);
      }}
      className={classNames(
        'cursor-pointer items-center rounded-[10px] max-w-[180px] bg-wdap_bonus_card_active_bg p-[20px] flex-shrink-0 border-[2px]',
        id === selectedBonus?.id
          ? 'flex flex-row border-wdap_bonus_card_active_border'
          : 'flex flex-col border-[transparent]'
      )}>
      <div className="select-none">
        <p className="font-medium leading-[130%] text-[12px] text-wdap_bonus_card_text">
          <strong className="text-[16px] leading-[130%] font-bold text-wdap_bonus_card_text">
            {freeSpins ? `${percent}% + ${freeSpins} ${t('freeSpins.fs')}` : `${percent}%`}
          </strong>
        </p>
        <p className="font-medium leading-[130%] text-[12px] text-wdap_bonus_card_text">
          {t('accountWalletDeposite.upTo')} {currencyIcon}
          {makeFloat(maxPayout[currencyName])}
        </p>
        <p className="font-medium leading-[130%] text-[12px] text-wdap_bonus_card_text">
          {t('accountWalletDeposite.forOneDepositFrom')} {currencyIcon}
          {makeFloat(minDepositAmount[currencyName])}
        </p>
      </div>
      <NavLink
        to={`/bonuses/${id}`}
        aria-label={`${name}`}
        className={id !== selectedBonus?.id ? 'pointer-events-none w-0 h-0' : 'pl-[10px]'}
        onClick={closeModal}>
        <Info fill={id === selectedBonus?.id ? 'var(--wdap_amount_text)' : 'transparent'} />
      </NavLink>
    </div>
  );
};
